import { useCallback, useRef, useState } from 'react';
import { apiFetch, readJsonResponse } from '../api/client';

/**
 * Open client LK in a new tab.
 * Open the tab synchronously on click (before await) — otherwise browsers block
 * window.open after the network round-trip.
 */
export function useOpenClientLk() {
  const [loadingUserId, setLoadingUserId] = useState(null);
  const [lastError, setLastError] = useState(null);
  const openingRef = useRef(false);

  const openClientLk = useCallback(async (userId) => {
    if (openingRef.current) {
      return;
    }

    const parsedUserId = Number.parseInt(userId, 10);

    if (!Number.isFinite(parsedUserId) || parsedUserId <= 0) {
      return;
    }

    openingRef.current = true;
    setLoadingUserId(parsedUserId);
    setLastError(null);

    // Must run in the same tick as the click — not after await.
    const popup = window.open('about:blank', '_blank');

    try {
      const response = await apiFetch(`/api/users/${parsedUserId}/lk-redirect`);
      const payload = await readJsonResponse(response, 'LK redirect');

      if (!response.ok) {
        throw new Error(payload.message || payload.error || `HTTP ${response.status}`);
      }

      const targetUrl = payload.redirect_url || payload.lk_url;

      if (!targetUrl) {
        throw new Error('Не получен URL для перехода в ЛК');
      }

      if (popup && !popup.closed) {
        popup.location.href = targetUrl;
      } else {
        // Popup blocked — same-tab fallback.
        window.location.assign(targetUrl);
      }
    } catch (openError) {
      console.error(openError);
      const message = openError?.message || 'Не удалось открыть ЛК';
      setLastError(message);
      if (popup && !popup.closed) {
        popup.close();
      }
      window.alert(`ЛК: ${message}`);
    } finally {
      openingRef.current = false;
      setLoadingUserId(null);
    }
  }, []);

  return {
    openClientLk,
    loadingUserId,
    lastError,
  };
}
